import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import { IcRemoveUserIcon, IcVoiceOff16Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopover, IPopoverMenuItem } from '@gem/ui-kit';
import { debounce } from '@gem/utils';

interface IOpponentVideoPresenterParams {
  opponent: Opponent;
  popover: IPopover<HTMLElement>;
  onClick?(): void;
  isHighlightAudio?: boolean;
}

interface IOpponentVideoPresenter {
  activeCall: Call;
  hasShowMute: boolean;
  hasShowRemove: boolean;
  actions: IPopoverMenuItem[];
  opponent: Opponent;
  popover: IPopover<HTMLElement>;
  isSpeaking: boolean;
  isMeOpponent: boolean;
  onPopoverClick?(): void;
  mute(): void;
  remove(): void;
  onClick(): void;
  playVideo(container: HTMLDivElement): void;
  stopVideo(): void;
  setOpponent(opponent: Opponent): void;
  setPopover(popover: IPopover<HTMLElement>): void;
  setIsSpeaking(isSpeaking: boolean): void;
  isRiseHand: boolean;
  canShowActionsButton: boolean;
}

export const useOpponentVideoPresenter = ({
  opponent,
  popover,
  onClick,
  isHighlightAudio,
}: IOpponentVideoPresenterParams): IOpponentVideoPresenter => {
  const { t } = useTranslation();
  const context = useCallContext();

  const presenter = useLocalObservable<IOpponentVideoPresenter>(() => ({
    opponent: opponent,
    popover: popover,
    onPopoverClick: onClick,
    isSpeaking: false,

    get isRiseHand(): boolean {
      return (
        !presenter.isSpeaking &&
        !!presenter.opponent.memberDevice?.isHandRaised &&
        !!isHighlightAudio &&
        !presenter.opponent.isSharedScreen
      );
    },

    get activeCall(): Call {
      return context.call;
    },

    get hasShowMute(): boolean {
      return (
        presenter.activeCall.isAvailableMuteAudio && !!presenter.opponent?.memberDevice?.isMuting
      );
    },

    get hasShowRemove(): boolean {
      return (
        presenter.activeCall.isAvailableRemoveMember &&
        !!presenter.opponent?.memberDevice?.isRemoving
      );
    },

    get canShowActionsButton(): boolean {
      if (this.activeCall.viewState.isMinimized) {
        return false;
      }
      return presenter.actions.length > 0;
    },

    mute(): void {
      if (presenter.opponent?.memberDevice && presenter.opponent?.memberDevice.deviceId) {
        context.callController.muteMember(
          presenter.opponent?.memberDevice.memberId,
          presenter.opponent?.memberDevice.deviceId
        );
      }
    },

    remove(): void {
      const channelId = presenter.activeCall.channelId;
      const userId = presenter.opponent?.memberDevice?.memberId;
      const deviceId = presenter.opponent?.memberDevice?.deviceId;

      if (!channelId || !userId || !deviceId) {
        return;
      }

      context.callController.removeMemberFromCall(userId, deviceId);
    },

    onClick(): void {
      popover.close();

      if (onClick) {
        onClick();
      }
    },

    playVideo(container: HTMLDivElement): void {
      context.callController?.playOpponentVideo(container, presenter.opponent.uid);
    },

    stopVideo(): void {
      context.callController.stopOpponentVideo(presenter.opponent.uid);
    },

    get isMeOpponent(): boolean {
      return context.call.providerUid === opponent.uid;
    },
    get actions(): IPopoverMenuItem[] {
      const actions = [];
      if (presenter.hasShowMute) {
        actions.push({
          key: 'mute',
          title: t('calls_mute_microphone'),
          onClick: presenter.mute,
          icon: IcVoiceOff16Icon,
        });
      }
      if (presenter.hasShowRemove) {
        actions.push({
          key: 'remove',
          title: t('common_remove'),
          onClick: presenter.remove,
          icon: IcRemoveUserIcon,
        });
      }
      return actions;
    },
    setOpponent(opponent: Opponent): void {
      presenter.opponent = opponent;
    },
    setPopover(popover: IPopover<HTMLElement>): void {
      presenter.popover = popover;
    },
    setIsSpeaking(isSpeaking: boolean): void {
      presenter.isSpeaking = isSpeaking;
    },
  }));

  useEffect(() => {
    presenter.setOpponent(opponent);
    presenter.setPopover(popover);
    presenter.onPopoverClick = onClick;
  }, [opponent, popover, onClick]);

  useEffect(() => {
    if (presenter.actions.length === 0) {
      popover.close();
    }
  }, [presenter.actions]);

  useEffect(() => {
    debounce(
      () => {
        presenter.setIsSpeaking(opponent.isActiveSpeaker);
      },
      1000,
      opponent.isActiveSpeaker || !opponent.hasAudio
    )();
  }, [opponent.isActiveSpeaker, opponent.hasAudio]);

  return presenter;
};
